import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
} from 'react';
import { usePerformedSearchRequest } from 'clientState/providers/SearchRequestProvider';
import updateFilterLegStates from '../../Filters/state/updateFilterLegStates';
import { useFilterTimeMetric } from 'clientState/hooks/metrics';
import { useAvailableConnections } from 'clientState/providers/ParsedSearchResponseProvider';
import toItineraryPredicate from '../../Filters/queries/toItineraryPredicate';
import {
  ConnectionFilterContextType,
  ConnectionFilterState,
  ConnectionFilterAction,
  UseLegFilterReturn,
  Airport,
  ConnectionFilterProviderProps,
} from './types';

const ConnectionFilterContext = createContext<ConnectionFilterContextType>([
  {},
  () => {},
]);

const isRoundTrip = (searchRequest) =>
  searchRequest && searchRequest.tripType === 'ROUND_TRIP';

const applyFilterReducer = (connections, action) =>
  updateFilterLegStates(action.legIds, action.connections, connections);

const reducer = (
  airports: ConnectionFilterState,
  action: ConnectionFilterAction
): ConnectionFilterState => {
  switch (action.type) {
    case 'RESET':
      return {};
    case 'FILTER':
      return applyFilterReducer(airports, action);
    default:
      return airports;
  }
};

const noop = ({ value }: { value: string }) => {};

const noLoadedHandlers = {
  airports: null,
  connections: null,
  toggle: noop,
};

const createDefaultAirports = (airports) =>
  airports &&
  airports.map((airport) => ({
    ...airport,
    checked: true,
  }));

const useLegFilter = (legId: number): UseLegFilterReturn => {
  const { start } = useFilterTimeMetric();
  const searchRequest = usePerformedSearchRequest();
  const availableConnections = useAvailableConnections();
  const [connections, dispatch] = useContext(ConnectionFilterContext);

  const currentConnections =
    (connections || [])[legId] ||
    createDefaultAirports((availableConnections || [])[legId]);

  const applyFilter = useMemo(() => {
    const legIds = isRoundTrip(searchRequest) ? [0, 1] : [legId];
    return (connections: Airport[]) => {
      start();
      dispatch({
        type: 'FILTER',
        legIds,
        connections,
      });
    };
  }, [legId, dispatch, searchRequest, start]);

  return [currentConnections, applyFilter];
};

export const useConnectionLegFilter = (legId: number) => {
  const [currentConnections, applyFilter] = useLegFilter(legId);
  if (!currentConnections) {
    return noLoadedHandlers;
  }

  return {
    connections: currentConnections,
    toggle({ value }: { value: string }) {
      const nextConnections = currentConnections.map((connection) =>
        connection.id.toString() === value.toString()
          ? {
              ...connection,
              checked: !connection.checked,
            }
          : connection
      );
      applyFilter(nextConnections);
    },
  };
};

const getId = ({ id }) => id;

const getCheckedAirportIds = (airports) =>
  new Set((airports || []).filter(({ checked }) => checked).map(getId));

const everyConnection = (predicate) => (sections) => {
  for (let index = 0; index < sections.length - 1; ++index) {
    const { destination } = sections[index];
    const { departure } = sections[index + 1];
    if (!predicate(destination) || !predicate(departure)) {
      return false;
    }
  }
  return true;
};

const alwaysTrue = () => true;

const createSegmentPredicate = (connections) => {
  const selectedStopIds = getCheckedAirportIds(connections);

  const hasStopAirports = connections
    ? everyConnection((connection) => selectedStopIds.has(connection.id))
    : alwaysTrue;

  return ({ sections }) => hasStopAirports(sections);
};

const createConnectionItineraryPredicate = toItineraryPredicate(
  createSegmentPredicate
);

export const useConnectionFilter = () => {
  const [connections, dispatch] = useContext(ConnectionFilterContext);
  const resetFilter = useCallback(
    () => dispatch({ type: 'RESET' }),
    [dispatch]
  );
  return {
    predicate: createConnectionItineraryPredicate(connections),
    resetFilter,
  };
};

export const useAppliedStopAirportFilter = () => {
  const [connections] = useContext(ConnectionFilterContext);
  if (!connections) return false;
  return Object.values(connections)
    .flat()
    .some(
      (
        { checked } = {
          id: 0,
          name: '',
          checked: false,
          iata: '',
          disabled: false,
        }
      ) => checked === false
    );
};

const ConnectionFilterProvider: React.FC<ConnectionFilterProviderProps> = ({
  children,
}) => {
  const stateAndDispatch = useReducer(reducer, []);

  return (
    <ConnectionFilterContext.Provider value={stateAndDispatch}>
      {children}
    </ConnectionFilterContext.Provider>
  );
};

export default ConnectionFilterProvider;
